
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import Logo from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon } from 'lucide-react';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const registerSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema>;

const Auth = () => {
  const { t } = useLanguage();
  const { signIn, signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if there's a redirect destination in the location state
  const redirectTo = location.state?.from?.pathname || '/';
  const registrationFlow = location.state?.registrationFlow || false;
  
  // Default to appropriate tab based on where the user is coming from
  const [activeTab, setActiveTab] = useState<string>(registrationFlow ? 'register' : 'login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmationNote, setShowConfirmationNote] = useState(false);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
    },
  });

  // Check for hash fragment for email confirmation
  useEffect(() => {
    const hash = location.hash;
    console.log('URL hash:', hash);
    // Check if this is a confirmation URL or other auth action
    if (hash && (hash.includes('type=signup') || hash.includes('type=recovery'))) {
      console.log('Detected auth action in URL');
    }
  }, [location]);

  useEffect(() => {
    // Prefill email from the registration flow if available
    if (registrationFlow && location.state?.projectName) {
      registerForm.setValue("fullName", location.state.projectName);
    }
  }, [registrationFlow, location.state, registerForm]);

  useEffect(() => {
    // If user is already logged in, redirect to the destination
    if (user && !loading) {
      console.log('User is authenticated, redirecting to:', redirectTo);
      navigate(redirectTo);
    }
  }, [user, loading, navigate, redirectTo]);

  const handleLogin = async (values: LoginFormValues) => {
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    setShowConfirmationNote(false);

    try {
      console.log('Attempting login with:', values.email);
      await signIn(values.email, values.password);
      setSuccessMessage("Login successful!");
      navigate(redirectTo);
    } catch (error: any) {
      console.error('Login error:', error.message);
      setError(error.message);
      
      // Check if the error is about email confirmation
      if (error.message.includes('Email not confirmed')) {
        setShowConfirmationNote(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (values: RegisterFormValues) => {
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    setShowConfirmationNote(false);

    try {
      console.log('Attempting signup with:', values.email, values.fullName);
      await signUp(values.email, values.password, values.fullName);
      setSuccessMessage("Registration successful! Please check your email (including spam folder) for a confirmation link.");
      setShowConfirmationNote(true);
      // Don't switch to login tab immediately so user can see the success message
    } catch (error: any) {
      console.error('Signup error:', error.message);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Logo className="mx-auto mb-4" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full flex justify-end mb-4">
        <LanguageSwitcher />
      </div>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Logo className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold">{t('auth.title')}</h1>
          <p className="text-gray-500">{t('auth.subtitle')}</p>
        </div>

        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <InfoIcon className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-700">Why create an account?</AlertTitle>
          <AlertDescription className="text-blue-600">
            Creating an account allows you to save your assessment results, 
            track changes over time, and access your data from any device. 
            Your assessments are private and only visible to you.
          </AlertDescription>
        </Alert>

        {successMessage && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <InfoIcon className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-700">Success</AlertTitle>
            <AlertDescription className="text-green-600">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        {showConfirmationNote && (
          <Alert className="mb-4 bg-yellow-50 border-yellow-200">
            <InfoIcon className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-700">Email Confirmation Required</AlertTitle>
            <AlertDescription className="text-yellow-600">
              Your account requires email confirmation before you can log in. Please check your email inbox and spam folder for a confirmation link. 
              If you haven't received it, you may need to contact support or try registering again.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(handleLogin)}>
                  <CardHeader>
                    <CardTitle>{t('auth.login')}</CardTitle>
                    <CardDescription>{t('auth.loginDesc')}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.email')}</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="you@example.com" 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.password')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {error && <p className="text-sm text-red-500">{error}</p>}
                  </CardContent>
                  
                  <CardFooter>
                    <Button className="w-full" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? t('common.loading') : t('auth.loginButton')}
                    </Button>
                  </CardFooter>
                </form>
              </Form>
            </TabsContent>
            
            <TabsContent value="register">
              <Form {...registerForm}>
                <form onSubmit={registerForm.handleSubmit(handleSignUp)}>
                  <CardHeader>
                    <CardTitle>{t('auth.register')}</CardTitle>
                    <CardDescription>{t('auth.registerDesc')}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <FormField
                      control={registerForm.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.fullName')}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={registerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.email')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="you@example.com" 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.password')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                          <p className="text-xs text-gray-500">Password must be at least 6 characters long</p>
                        </FormItem>
                      )}
                    />
                    
                    {error && <p className="text-sm text-red-500">{error}</p>}
                  </CardContent>
                  
                  <CardFooter>
                    <Button className="w-full" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? t('common.loading') : registrationFlow ? 'Register and Start Assessment' : t('auth.registerButton')}
                    </Button>
                  </CardFooter>
                </form>
              </Form>
            </TabsContent>
          </Tabs>
        </Card>
        
        <div className="mt-4 text-center text-sm text-gray-500">
          <Button variant="link" onClick={() => navigate('/')}>
            {t('auth.back')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Auth;
